import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { Users, CheckCircle, XCircle, Plus, Trash2, ClipboardList, XOctagon, DollarSign, Scissors, Undo2 } from 'lucide-react';
import ReciboManager from './ReciboManager';
import { formatEcuadorTime, getOrderCode } from '../../utils/ecuadorTime';
import CustomAlert from './shared/CustomAlert';

const PERSON_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-400', btn: 'bg-blue-600 hover:bg-blue-700', label: 'bg-blue-600' },
  { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-400', btn: 'bg-purple-600 hover:bg-purple-700', label: 'bg-purple-600' },
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-400', btn: 'bg-orange-600 hover:bg-orange-700', label: 'bg-orange-600' },
  { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-400', btn: 'bg-pink-600 hover:bg-pink-700', label: 'bg-pink-600' },
];

// ⭐ Límites de mesas por plan
const TABLE_LIMITS = {
  basic: 6,
  standard: 15,
  premium: 30,
  enterprise: Infinity
};

// ⭐ Zona horaria + código de pedido por día ahora viven en un solo lugar
// compartido (utils/ecuadorTime.js, ver ese archivo para el porqué). Nota:
// getOrderCode ya no agrega "(#id)" al final -- mismo formato que Cocina y
// Pedidos Pendientes (consistente con el fix que ya había quitado ese "(#id)"
// de Reportes y Cocina).

function MesasManager({ token, clientData }) {
  // ⭐ Editar un pedido ya pagado es un permiso aparte (pedido de Angelo,
  // 2026-08-05): el dueño siempre puede, un empleado solo si se le asignó
  // 'editar_pedidos_pagados' explícitamente en Empleados -- no viene
  // incluido con 'caja'/'mesas'/'pedidos'.
  const canEditPaidOrders = clientData?.role === 'owner' || !!clientData?.permissions?.editar_pedidos_pagados;
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [mixedCash, setMixedCash] = useState('');
  const [mixedTransfer, setMixedTransfer] = useState('');
  const [customAlert, setCustomAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [newTableName, setNewTableName] = useState('');
  const [pendingReceipt, setPendingReceipt] = useState(null);

  // ⭐ NUEVO: Estados para pedidos sin mesa
  const [showUnassignedModal, setShowUnassignedModal] = useState(false);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [showAssignTableModal, setShowAssignTableModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [confirmRemoveItem, setConfirmRemoveItem] = useState(null); // { orderId, origIdx, name, quantity }
  const [removingItem, setRemovingItem] = useState(false);

  // ⭐ Estados para vista de cuenta de mesa y desglose
  const [tableOrders, setTableOrders] = useState([]);
  const [loadingTableOrders, setLoadingTableOrders] = useState(false);
  // ⭐ Historial de comandas YA pagadas de la mesa. Antes era solo lectura y
  // no participaba en el total a cobrar ni en "Liberar Mesa" (eso sigue
  // igual, usa únicamente `tableOrders`) -- ahora además se puede EDITAR
  // cada comanda pagada (pedido de Angelo, 2026-08-05), ver
  // editingPaidOrder/handleSavePaidEdit más abajo y PUT /sales/:id/edit-paid.
  const [paidOrders, setPaidOrders] = useState([]);
  const [loadingPaidOrders, setLoadingPaidOrders] = useState(false);
  const [editingPaidOrder, setEditingPaidOrder] = useState(null); // la orden pagada que se está editando
  const [editItems, setEditItems] = useState([]); // copia editable de items mientras se edita
  const [editReason, setEditReason] = useState('');
  const [savingPaidEdit, setSavingPaidEdit] = useState(false);
  const [showSplitBillModal, setShowSplitBillModal] = useState(false);
  const [splitPersons, setSplitPersons] = useState([]);
  const [splitUnits, setSplitUnits] = useState([]); // ítems explotados por unidad
  const [splitHistory, setSplitHistory] = useState([]); // historial para deshacer
  const [splitPayingPerson, setSplitPayingPerson] = useState(null);
  const [splitCash, setSplitCash] = useState('');
  const [splitPayMethod, setSplitPayMethod] = useState('cash');
  const [splitMixedCashBill, setSplitMixedCashBill] = useState('');
  const [splitMixedTransferBill, setSplitMixedTransferBill] = useState('');
  const [splitProcessing, setSplitProcessing] = useState(false);

  const showAlert = (type, title, message) => {
    setCustomAlert({ type, title, message });
  };

  useEffect(() => {
    loadTables();
    const interval = setInterval(() => {
      loadTables();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTables(response.data);
    } catch (error) {
      console.error('Error al cargar mesas:', error);
    }
  };

  // ⭐ NUEVO: Cargar pedidos sin mesa
  const loadUnassignedOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/sales/unassigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnassignedOrders(response.data);
    } catch (error) {
      console.error('Error al cargar pedidos sin mesa:', error);
    }
  };

  // Cargar órdenes de una mesa
  const loadTableOrders = async (tableId) => {
    setLoadingTableOrders(true);
    try {
      const response = await axios.get(`${API_URL}/api/pos/tables/${tableId}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTableOrders(response.data || []);
    } catch (error) {
      console.error('Error al cargar órdenes de mesa:', error);
      setTableOrders([]);
    } finally {
      setLoadingTableOrders(false);
    }
  };

  // ⭐ Comandas ya pagadas de esta mesa (hoy, desde que se ocupó esta vez --
  // ver comentario del backend en GET /api/pos/tables/:id/orders?status=paid).
  // Separado de loadTableOrders a propósito: son dos listas con semántica
  // distinta (una es "falta cobrar", la otra es "ya se cobró, disponible
  // para consulta/edición"), no tiene sentido mezclarlas en un solo estado.
  const loadPaidOrders = async (tableId) => {
    setLoadingPaidOrders(true);
    try {
      const response = await axios.get(`${API_URL}/api/pos/tables/${tableId}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: 'paid' }
      });
      setPaidOrders(response.data || []);
    } catch (error) {
      console.error('Error al cargar comandas pagadas de mesa:', error);
      setPaidOrders([]);
    } finally {
      setLoadingPaidOrders(false);
    }
  };

  const openEditPaidOrder = (order) => {
    setEditingPaidOrder(order);
    setEditItems((order.items || []).map(it => ({ ...it })));
    setEditReason('');
  };

  const updateEditItemQty = (idx, delta) => {
    setEditItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const newQty = Math.max(0, (Number(it.quantity) || 0) + delta);
      return { ...it, quantity: newQty };
    }).filter(it => it.quantity > 0));
  };

  const editTotal = editItems.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);

  const handleSavePaidEdit = async () => {
    if (!editingPaidOrder) return;
    if (!editReason.trim()) {
      showAlert('warning', 'Motivo requerido', 'Explica por qué se está editando este pedido ya pagado (queda en Auditoría).');
      return;
    }
    if (editItems.length === 0) {
      showAlert('warning', 'Pedido vacío', 'Un pedido editado no puede quedar sin productos. Si quieres anularlo, hazlo desde su opción de anular.');
      return;
    }
    setSavingPaidEdit(true);
    try {
      await axios.put(
        `${API_URL}/api/pos/sales/${editingPaidOrder.id}/edit-paid`,
        { items: editItems, reason: editReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Pedido editado', 'El pedido pagado fue actualizado. El cambio quedó registrado en Auditoría.');
      setEditingPaidOrder(null);
      if (selectedTable) loadPaidOrders(selectedTable.id);
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo editar el pedido');
    } finally {
      setSavingPaidEdit(false);
    }
  };

  // ⭐ NUEVO: Asignar mesa a un pedido
  const handleAssignTable = async (orderId, tableId) => {
    try {
      await axios.post(
        `${API_URL}/api/pos/sales/${orderId}/assign-table`,
        { table_id: tableId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Mesa Asignada', ' La mesa fue asignada al pedido exitosamente');
      setShowAssignTableModal(false);
      setSelectedOrder(null);
      loadUnassignedOrders();
      loadTables();
    } catch (error) {
      console.error('Error:', error);
      showAlert('error', 'Error', ` ${error.response?.data?.error || 'No se pudo asignar la mesa'}`);
    }
  };

  // ⭐ NUEVO: Anular pedido con motivo
  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      showAlert('warning', 'Motivo Requerido', ' Debes ingresar el motivo de la anulación');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/pos/sales/${selectedOrder.id}/cancel`,
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Pedido Anulado', `Pedido ${getOrderCode(selectedOrder)} anulado\n\nMotivo: ${cancelReason}`);
      setShowCancelModal(false);
      setSelectedOrder(null);
      setCancelReason('');
      loadTables();
      loadUnassignedOrders();
      // Refrescar órdenes de la mesa si hay una seleccionada
      if (selectedTable) {
        try {
          const res = await axios.get(`${API_URL}/api/pos/tables/${selectedTable.id}/orders`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const parsed = (res.data || []).map(o => ({
            ...o,
            items: typeof o.items === 'string' ? JSON.parse(o.items) : o.items
          }));
          setTableOrders(parsed);
          // Si ya no quedan órdenes, cerrar el modal de mesa
          if (parsed.length === 0) setSelectedTable(null);
        } catch (_) {}
      }
    } catch (error) {
      console.error('Error:', error);
      showAlert('error', 'Error', ` ${error.response?.data?.error || 'No se pudo anular el pedido'}`);
    }
  };

  // ⭐ Anular UN producto especifico dentro de una comanda de la mesa (ej: se
  // acabó ese plato), sin anular toda la comanda. Si era el último producto,
  // el backend anula la comanda completa (y libera la mesa si ya no le
  // quedan otras comandas activas).
  const executeRemoveItem = async () => {
    if (!confirmRemoveItem) return;
    setRemovingItem(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/pos/sales/${confirmRemoveItem.orderId}/remove-item`,
        { origIdx: confirmRemoveItem.origIdx, quantity: confirmRemoveItem.quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert(
        'success',
        response.data.cancelled ? 'Comanda anulada' : 'Producto anulado',
        response.data.cancelled
          ? `"${confirmRemoveItem.name}" era el único producto de esa comanda; la comanda quedó anulada.`
          : `"${confirmRemoveItem.name}" fue quitado de la comanda.`
      );
      setConfirmRemoveItem(null);
      loadTables();
      if (selectedTable) {
        try {
          const res = await axios.get(`${API_URL}/api/pos/tables/${selectedTable.id}/orders`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const parsed = (res.data || []).map(o => ({
            ...o,
            items: typeof o.items === 'string' ? JSON.parse(o.items) : o.items
          }));
          setTableOrders(parsed);
          if (parsed.length === 0) setSelectedTable(null);
        } catch (_) {}
      }
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo anular el producto');
    } finally {
      setRemovingItem(false);
    }
  };

  const handleCreateTable = async () => {
    if (!newTableName.trim()) {
      showAlert('warning', 'Nombre Requerido', ' Ingresa el nombre de la mesa');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/pos/tables`,
        { name: newTableName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Mesa Creada', ` Mesa "${newTableName}" creada exitosamente`);
      setShowCreateModal(false);
      setNewTableName('');
      loadTables();
    } catch (error) {
      console.error('Error:', error);
      showAlert('error', 'Error', ` ${error.response?.data?.error || 'No se pudo crear la mesa'}`);
    }
  };

  const handleDeleteTable = (tableId, tableName) => {
    setConfirmDelete({ id: tableId, name: tableName });
  };

  const executeDeleteTable = async (tableId, tableName) => {
    setConfirmDelete(null);
    try {
      await axios.delete(`${API_URL}/api/pos/tables/${tableId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showAlert('success', 'Mesa Eliminada', `Mesa "${tableName}" eliminada`);
      loadTables();
    } catch (error) {
      console.error('Error:', error);
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo eliminar la mesa');
    }
  };

  const handleFinishTable = async (tableId) => {
    try {
      await axios.post(
        `${API_URL}/api/pos/tables/${tableId}/finish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Mesa Liberada', ' Mesa marcada como disponible');
      loadTables();
      setSelectedTable(null);
      setTableOrders([]);
    } catch (error) {
      console.error('Error:', error);
      showAlert('error', 'Error', ` ${error.response?.data?.error || 'No se pudo liberar la mesa'}`);
    }
  };

  const handlePayTable = (table) => {
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    if (!selectedTable) return;

    const total = tableOrders.length > 0
      ? tableOrders.reduce((sum, o) => sum + Number(o.total), 0)
      : Number(selectedTable.total || 0);

    if (total <= 0) {
      showAlert('warning', 'Sin total', 'No hay monto a cobrar en esta mesa.');
      return;
    }
    if (paymentMethod === 'cash') {
      const received = parseFloat(cashReceived) || 0;
      if (received < total) {
        showAlert('error', 'Monto Insuficiente', ` Recibido: $${received.toFixed(2)}\n Total: $${total.toFixed(2)}`);
        return;
      }
    } else if (paymentMethod === 'mixed') {
      const cash = parseFloat(mixedCash) || 0;
      const transfer = parseFloat(mixedTransfer) || 0;
      if (cash + transfer < total) {
        showAlert('error', 'Monto Insuficiente', ` Recibido: $${(cash + transfer).toFixed(2)}\n Total: $${total.toFixed(2)}`);
        return;
      }
    }

    try {
      const cashR = paymentMethod === 'cash' ? parseFloat(cashReceived) : paymentMethod === 'mixed' ? (parseFloat(mixedCash) || 0) : null;
      const transferR = paymentMethod === 'transfer' ? total : paymentMethod === 'mixed' ? (parseFloat(mixedTransfer) || 0) : null;
      const changeR = paymentMethod === 'cash'
        ? (parseFloat(cashReceived) - total)
        : paymentMethod === 'mixed'
          ? Math.max(0, (parseFloat(mixedCash) || 0) + (parseFloat(mixedTransfer) || 0) - total)
          : null;

      await axios.post(
        `${API_URL}/api/pos/tables/${selectedTable.id}/pay`,
        {
          payment_method: paymentMethod,
          cash_received: cashR,
          transfer_amount: transferR
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // ⭐ No se cierra el modal/mesa aqui: se difiere a onDone (ver
      // ReciboManager), que corre despues de que el usuario decide si
      // imprime el recibo.
      const flatItems = tableOrders.flatMap(o =>
        (Array.isArray(o.items) ? o.items : []).map(it => ({
          name: it.name, quantity: it.quantity, subtotal: it.subtotal
        }))
      );
      setPendingReceipt({
        ticket_ref: `Mesa ${selectedTable.name}`,
        items: flatItems.length > 0 ? flatItems : [{ name: 'Consumo', quantity: 1, subtotal: total }],
        total,
        payment_method: paymentMethod,
        cash_received: cashR,
        transfer_amount: transferR,
        change_given: changeR,
        order_type: 'servir',
        table_label: selectedTable.name,
        created_at: new Date().toISOString(),
        onDone: () => {
          setShowPaymentModal(false);
          setSelectedTable(null);
          setTableOrders([]);
          setCashReceived('');
          setMixedCash('');
          setMixedTransfer('');
          setPaymentMethod('cash');
          setPendingReceipt(null);
          loadTables();
        }
      });
    } catch (error) {
      console.error('Error:', error);
      showAlert('error', 'Error al Cobrar', ` ${error.response?.data?.error || 'No se pudo procesar el pago'}`);
    }
  };

  const getTableColor = (table) => {
    if (table.status === 'available') return 'bg-green-500';
    if (table.order_count > 0) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getTableStatus = (table) => {
    if (table.status === 'available') return 'Disponible';
    if (table.order_count > 0) return `Ocupada - ${table.order_count} pendiente${table.order_count !== 1 ? 's' : ''}`;
    if (table.payment_status === 'paid') return 'Ocupada - Pagado';
    return 'Ocupada - Pendiente';
  };

  const getTableTotal = () => {
    if (!selectedTable) return 0;
    return tableOrders.length > 0
      ? tableOrders.reduce((sum, o) => sum + Number(o.total), 0)
      : Number(selectedTable.total || 0);
  };

  const getChange = () => {
    const received = parseFloat(cashReceived) || 0;
    return Math.max(0, received - getTableTotal());
  };

  const getMixedChange = () => {
    const cash = parseFloat(mixedCash) || 0;
    const transfer = parseFloat(mixedTransfer) || 0;
    return Math.max(0, (cash + transfer) - getTableTotal());
  };

  return (
    <div>
      {customAlert && (
        <CustomAlert
          type={customAlert.type}
          title={customAlert.title}
          message={customAlert.message}
          onClose={() => setCustomAlert(null)}
          zIndex={70}
        />
      )}
      <ReciboManager pendingReceipt={pendingReceipt} businessName={clientData?.business_name} clientPhone={clientData?.phone} token={token} />

      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Eliminar mesa</h3>
            <p className="text-gray-600 text-sm mb-6">
              ¿Eliminar la mesa <strong>{confirmDelete.name}</strong>? Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button onClick={() => executeDeleteTable(confirmDelete.id, confirmDelete.name)} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* ⭐ SECCIN PRINCIPAL: Mesas */}
      <div>
        {/* ⭐ Barra de progreso de mesas según plan */}
        {(() => {
          const plan = clientData?.plan || 'basic';
          const tableLimit = TABLE_LIMITS[plan] || TABLE_LIMITS.basic;
          const tableCount = tables.length;
          const tablePercent = tableLimit === Infinity ? 0 : Math.min(100, (tableCount / tableLimit) * 100);
          const atLimit = tableLimit !== Infinity && tableCount >= tableLimit;

          return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Mesas del plan</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold capitalize">
                    {plan}
                  </span>
                </div>
                <span className={`text-sm font-bold ${atLimit ? 'text-red-600' : 'text-blue-600'}`}>
                  {tableCount} / {tableLimit === Infinity ? '' : tableLimit} mesas
                </span>
              </div>
              {tableLimit !== Infinity ? (
                <>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        tablePercent >= 100 ? 'bg-red-500' : tablePercent >= 75 ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${tablePercent}%` }}
                    ></div>
                  </div>
                  {atLimit ? (
                    <p className="text-xs text-red-600 mt-1 font-semibold">
                       Límite de mesas alcanzado · Actualiza tu plan para agregar más
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Puedes agregar {tableLimit - tableCount} mesa{tableLimit - tableCount !== 1 ? 's' : ''} más con tu plan {plan}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs text-green-600 font-semibold mt-1"> Plan Enterprise · Mesas ilimitadas</p>
              )}
            </div>
          );
        })()}

        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-8 h-8 text-blue-600" />
              Gestión de Mesas
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {tables.filter(t => t.status === 'available').length} disponibles 
              {tables.filter(t => t.status === 'occupied').length} ocupadas
            </p>
          </div>

          <div className="flex gap-3">
            {/* ⭐ NUEVO: BOTN PEDIDOS SIN MESA */}
            <button
              onClick={() => {
                loadUnassignedOrders();
                setShowUnassignedModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
            >
              <ClipboardList className="w-5 h-5" />
              Pedidos Sin Mesa
            </button>

            {/* ⭐ BOTN CREAR MESA */}
            {(() => {
              const plan = clientData?.plan || 'basic';
              const tableLimit = TABLE_LIMITS[plan] || TABLE_LIMITS.basic;
              const atLimit = tableLimit !== Infinity && tables.length >= tableLimit;
              return (
                <button
                  onClick={() => {
                    if (atLimit) {
                      showAlert('warning', 'Límite de Plan', `️ Tu plan ${plan.toUpperCase()} permite máximo ${tableLimit} mesas. Actualiza tu plan para agregar más.`);
                      return;
                    }
                    setShowCreateModal(true);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                    atLimit ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <Plus className="w-5 h-5" />
                  Nueva Mesa
                </button>
              );
            })()}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {tables.map((table) => (
            <div
              key={table.id}
              className={`${getTableColor(table)} text-white rounded-lg p-6 shadow-lg relative group`}
            >
              {/* Botón eliminar (solo si está disponible) */}
              {table.status === 'available' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteTable(table.id, table.name);
                  }}
                  className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}

              <div
                className="text-center cursor-pointer"
                onClick={() => {
                  if (table.status === 'occupied') {
                    setSelectedTable(table);
                    loadTableOrders(table.id);
                    loadPaidOrders(table.id);
                  } else {
                    setCustomAlert({
                      type: 'success',
                      title: `✅ Mesa ${table.name} — Libre`,
                      message: 'Esta mesa está disponible. Para asignarle una orden, ve a la pestaña "Caja", selecciona los productos y elige esta mesa antes de procesar el pago o dejar el pedido pendiente.'
                    });
                  }
                }}
              >
                <Users className="w-12 h-12 mx-auto mb-3" />
                <h3 className="text-xl font-bold mb-2">{table.name}</h3>
                <p className="text-sm opacity-90">{getTableStatus(table)}</p>
                {table.status === 'occupied' && table.order_count > 0 && (
                  <>
                    <p className="text-xs opacity-80 mt-1">
                      {table.order_count} orden{table.order_count !== 1 ? 'es' : ''}
                    </p>
                    <p className="text-2xl font-bold mt-2">${table.total?.toFixed(2)}</p>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {tables.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>No hay mesas configuradas</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 text-blue-600 hover:underline"
            >
              Crear primera mesa
            </button>
          </div>
        )}
      </div>

      {/* ⭐ MODAL CREAR MESA */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Crear Nueva Mesa</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de la Mesa *
              </label>
              <input
                type="text"
                value={newTableName}
                onChange={(e) => setNewTableName(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Mesa 1"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewTableName('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateTable}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalle de Mesa - Cuenta Unificada */}
      {selectedTable && !showPaymentModal && !showSplitBillModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="p-5 border-b flex-shrink-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white">{selectedTable.name}</h3>
                  <p className="text-sm text-blue-100 mt-0.5">
                    {Number(selectedTable.order_count) > 0
                      ? `${selectedTable.order_count} orden${Number(selectedTable.order_count) !== 1 ? 'es' : ''} pendiente${Number(selectedTable.order_count) !== 1 ? 's' : ''}`
                      : 'Sin órdenes pendientes'}
                  </p>
                </div>
                <button onClick={() => { setSelectedTable(null); setTableOrders([]); setPaidOrders([]); }} className="text-white hover:bg-white hover:bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold">×</button>
              </div>
              {Number(selectedTable.order_count) > 0 && (
                <div className="mt-2 text-white text-right">
                  <span className="text-3xl font-bold">${Number(selectedTable.total || 0).toFixed(2)}</span>
                  <p className="text-xs text-blue-200">Total cuenta</p>
                </div>
              )}
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              {loadingTableOrders ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-sm">Cargando órdenes...</p>
                </div>
              ) : tableOrders.length > 0 ? (
                <>
                  <h4 className="font-semibold mb-3 text-gray-700 text-sm uppercase tracking-wide">Cuenta de la mesa:</h4>
                  <div className="space-y-3 mb-4">
                    {tableOrders.map((order) => (
                      <div key={order.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-center mb-2">
                          <p className="font-bold text-sm text-gray-800">Orden {getOrderCode(order)}</p>
                          <p className="text-xs text-gray-500">
                            {formatEcuadorTime(order.created_at)}
                          </p>
                        </div>
                        <div className="space-y-1">
                          {(Array.isArray(order.items) ? order.items : []).map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm gap-2">
                              <span className="text-gray-700 flex-1 min-w-0">{item.quantity}x {item.name}</span>
                              <span className="font-semibold text-gray-900 flex-shrink-0">${item.subtotal?.toFixed(2)}</span>
                              <button
                                onClick={() => setConfirmRemoveItem({ orderId: order.id, origIdx: idx, name: item.name, quantity: item.quantity })}
                                className="flex-shrink-0 p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition"
                                title="Anular este producto"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-gray-200 mt-2 pt-1.5 flex justify-between items-center">
                          <span className="text-xs font-semibold text-gray-500">Subtotal:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm text-gray-800">${Number(order.total).toFixed(2)}</span>
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowCancelModal(true);
                              }}
                              className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded hover:bg-red-100 font-semibold"
                            >
                              Anular
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t-2 border-gray-300 pt-3 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-800">TOTAL:</span>
                      <span className="text-3xl font-bold text-green-600">
                        ${tableOrders.reduce((sum, o) => sum + Number(o.total), 0).toFixed(2)}
                      </span>
                    </div>
                    {tableOrders.length > 1 && (
                      <p className="text-xs text-gray-500 mt-1">{tableOrders.length} órdenes combinadas</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No hay órdenes pendientes en esta mesa</p>
                </div>
              )}

              {/* ⭐ Comandas ya pagadas de esta mesa (pedido de Angelo, 2026-08-05):
                  se muestran de nuevo (solo hoy, desde que la mesa se ocupó esta
                  vez) y ahora además se pueden editar -- ver openEditPaidOrder.
                  No participan del total a cobrar de arriba ni de "Liberar Mesa",
                  eso sigue basado únicamente en tableOrders. */}
              {!loadingPaidOrders && paidOrders.length > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold mb-3 text-gray-700 text-sm uppercase tracking-wide flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-green-600" /> Comandas ya pagadas hoy
                  </h4>
                  <div className="space-y-2">
                    {paidOrders.map((order) => (
                      <div key={order.id} className="bg-green-50 rounded-lg p-3 border border-green-200">
                        <div className="flex justify-between items-center mb-1.5">
                          <p className="font-bold text-sm text-gray-800">Orden {getOrderCode(order)}</p>
                          <p className="text-xs text-gray-500">{formatEcuadorTime(order.created_at)}</p>
                        </div>
                        <div className="space-y-0.5">
                          {(Array.isArray(order.items) ? order.items : []).map((item, idx) => (
                            <div key={idx} className="flex justify-between text-xs text-gray-600">
                              <span>{item.quantity}x {item.name}</span>
                              <span>${item.subtotal?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-green-200 mt-1.5 pt-1.5 flex justify-between items-center">
                          <span className="font-bold text-sm text-gray-800">${Number(order.total).toFixed(2)}</span>
                          {canEditPaidOrders && (
                            <button
                              onClick={() => openEditPaidOrder(order)}
                              className="text-xs bg-white text-green-700 border border-green-300 px-2 py-0.5 rounded hover:bg-green-100 font-semibold"
                            >
                              Editar
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {tableOrders.length > 0 ? (
                  <>
                    <button
                      onClick={() => handlePayTable(selectedTable)}
                      className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition flex items-center justify-center gap-2"
                    >
                      <DollarSign className="w-5 h-5" />
                      Cobrar Cuenta completa
                    </button>
                    <button
                      onClick={() => {
                        // Explotar todos los items en unidades individuales
                        const units = [];
                        tableOrders.forEach(order => {
                          const items = Array.isArray(order.items) ? order.items : [];
                          items.forEach((item, origIdx) => {
                            for (let u = 0; u < (item.quantity || 1); u++) {
                              units.push({
                                ...item,
                                quantity: 1,
                                subtotal: item.price,
                                orderId: order.id,
                                _origIdx: origIdx,
                                _unitNum: u + 1,
                                _totalUnits: item.quantity || 1,
                              });
                            }
                          });
                        });
                        setSplitUnits(units);
                        setSplitPersons([
                          { id: 1, name: 'Persona 1', itemIndices: units.map((_, i) => i), paymentDone: false },
                          { id: 2, name: 'Persona 2', itemIndices: [], paymentDone: false },
                        ]);
                        setSplitPayingPerson(null);
                        setSplitCash('');
                        setSplitPayMethod('cash');
                        setSplitMixedCashBill('');
                        setSplitMixedTransferBill('');
                        setSplitHistory([]);
                        setShowSplitBillModal(true);
                      }}
                      className="w-full border-2 border-purple-500 text-purple-700 py-3 rounded-lg font-bold hover:bg-purple-50 transition flex items-center justify-center gap-2"
                    >
                      <Scissors className="w-5 h-5" />
                      Desglosar cuenta (pago separado)
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleFinishTable(selectedTable.id)}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-bold"
                  >
                    Liberar Mesa
                  </button>
                )}
                <button
                  onClick={() => { setSelectedTable(null); setTableOrders([]); setPaidOrders([]); }}
                  className="w-full px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pago */}
      {showPaymentModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold">Cobrar Mesa</h3>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                  }}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-600">Mesa: {selectedTable.name}</p>
                <p className="text-3xl font-bold text-green-600">${getTableTotal().toFixed(2)}</p>
              </div>
            </div>

            <div className="p-6">
              <h4 className="font-semibold mb-3">Método de Pago</h4>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <button
                  onClick={() => {
                    setPaymentMethod('cash');
                    setMixedCash('');
                    setMixedTransfer('');
                  }}
                  className={`p-4 border-2 rounded-lg font-semibold transition-all ${
                    paymentMethod === 'cash'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                   Efectivo
                </button>
                <button
                  onClick={() => {
                    setPaymentMethod('transfer');
                    setCashReceived('');
                    setMixedCash('');
                    setMixedTransfer('');
                  }}
                  className={`p-4 border-2 rounded-lg font-semibold transition-all ${
                    paymentMethod === 'transfer'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                   Transfer
                </button>
                <button
                  onClick={() => {
                    setPaymentMethod('mixed');
                    setCashReceived('');
                  }}
                  className={`p-4 border-2 rounded-lg font-semibold transition-all ${
                    paymentMethod === 'mixed'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                   Mixto
                </button>
              </div>

              {paymentMethod === 'cash' && (
                <div className="mb-4">
                  <label className="block font-semibold mb-2">
                     Efectivo recibido <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg text-xl"
                    placeholder="0.00"
                    autoFocus
                  />
                  {cashReceived && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-gray-600"> Cambio:</p>
                      <p className="text-3xl font-bold text-green-600">
                        ${getChange().toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {paymentMethod === 'transfer' && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600">Monto a transferir:</p>
                  <p className="text-2xl font-bold text-blue-600">${getTableTotal().toFixed(2)}</p>
                </div>
              )}

              {paymentMethod === 'mixed' && (
                <div className="mb-4 space-y-4">
                  <div>
                    <label className="block font-semibold mb-2">
                       Efectivo <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={mixedCash}
                      onChange={(e) => setMixedCash(e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg text-xl"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-2">
                       Transferencia <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={mixedTransfer}
                      onChange={(e) => setMixedTransfer(e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg text-xl"
                      placeholder="0.00"
                    />
                  </div>
                  {mixedCash && mixedTransfer && (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm">Total recibido:</span>
                        <span className="font-bold">${(parseFloat(mixedCash) + parseFloat(mixedTransfer)).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm"> Cambio:</span>
                        <span className="text-2xl font-bold text-green-600">${getMixedChange().toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={processPayment}
                className="w-full bg-green-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-colors"
              >
                Confirmar Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⭐ MODAL: DESGLOSAR CUENTA POR UNIDAD (SPLIT BILL) */}
      {showSplitBillModal && selectedTable && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[55] p-4"
          onClick={() => { if (splitPayingPerson === null) setShowSplitBillModal(false); }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white rounded-t-2xl border-b p-5 flex justify-between items-center z-10">
              <div>
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Scissors className="w-6 h-6 text-purple-600" />
                  Desglosar Cuenta — {selectedTable.name}
                </h3>
                <p className="text-sm text-gray-500">
                  Total: <strong className="text-green-600">${tableOrders.reduce((sum, o) => sum + Number(o.total), 0).toFixed(2)}</strong>
                </p>
              </div>
              <button onClick={() => setShowSplitBillModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Controles */}
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => {
                  // Repartir equitativamente
                  const n = splitPersons.length;
                  if (n < 2) return;
                  const newPersons = splitPersons.map((p, pIdx) => ({
                    ...p,
                    itemIndices: splitUnits
                      .map((_, i) => i)
                      .filter((_, i) => i % n === pIdx)
                  }));
                  setSplitHistory(prev => [...prev, JSON.parse(JSON.stringify(splitPersons))]);
                  setSplitPersons(newPersons);
                }}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">
                  ⚖️ Repartir equitativamente
                </button>
                {splitPersons.length < 4 && (
                  <button onClick={() => {
                    setSplitPersons(prev => [
                      ...prev,
                      { id: prev.length + 1, name: `Persona ${prev.length + 1}`, itemIndices: [], paymentDone: false }
                    ]);
                  }}
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 transition">
                    + Agregar persona
                  </button>
                )}
                {splitHistory.length > 0 && (
                  <button onClick={() => {
                    const prev = splitHistory[splitHistory.length - 1];
                    setSplitPersons(prev);
                    setSplitHistory(h => h.slice(0, -1));
                  }}
                    className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm font-semibold hover:bg-orange-200 transition flex items-center gap-1">
                    <Undo2 className="w-3.5 h-3.5" /> Deshacer ({splitHistory.length})
                  </button>
                )}
              </div>

              {/* Unidades individuales y asignación */}
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Asignar unidades a cada persona ({splitUnits.length} unidades)</p>
                </div>
                <div className="divide-y max-h-60 overflow-y-auto">
                  {splitUnits.map((unit, unitIdx) => {
                    const assignedTo = splitPersons.findIndex(p => p.itemIndices.includes(unitIdx));
                    return (
                      <div key={unitIdx} className="px-4 py-2.5 flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 text-sm truncate">
                            {unit.name}
                            {unit._totalUnits > 1 && (
                              <span className="text-gray-400 text-xs ml-1">({unit._unitNum}/{unit._totalUnits})</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">${(unit.price || 0).toFixed(2)}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {splitPersons.map((person, pIdx) => {
                            const isAssigned = assignedTo === pIdx;
                            const colors = PERSON_COLORS[pIdx] || PERSON_COLORS[0];
                            return (
                              <button
                                key={pIdx}
                                onClick={() => {
                                  if (person.paymentDone) return;
                                  setSplitHistory(prev => [...prev, JSON.parse(JSON.stringify(splitPersons))]);
                                  setSplitPersons(prev => prev.map((p, i) => {
                                    if (i === pIdx) {
                                      const alreadyHas = p.itemIndices.includes(unitIdx);
                                      return { ...p, itemIndices: alreadyHas ? p.itemIndices.filter(j => j !== unitIdx) : [...p.itemIndices, unitIdx] };
                                    } else {
                                      return { ...p, itemIndices: p.itemIndices.filter(j => j !== unitIdx) };
                                    }
                                  }));
                                }}
                                disabled={person.paymentDone}
                                className={`w-8 h-8 rounded-full text-xs font-black transition ${
                                  isAssigned
                                    ? `${colors.label} text-white shadow-md scale-110`
                                    : person.paymentDone
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                    : `bg-gray-100 text-gray-500 hover:${colors.bg}`
                                }`}
                                title={person.name}
                              >
                                P{pIdx + 1}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Personas y pagos */}
              <div className="space-y-3">
                {splitPersons.map((person, pIdx) => {
                  const total = parseFloat(person.itemIndices.reduce((sum, i) => sum + (splitUnits[i]?.price || 0), 0).toFixed(2));
                  const colors = PERSON_COLORS[pIdx] || PERSON_COLORS[0];
                  const isPaying = splitPayingPerson === pIdx;

                  return (
                    <div key={pIdx}
                      className={`rounded-xl border-2 overflow-hidden ${person.paymentDone ? 'border-green-400 bg-green-50' : `${colors.border} ${colors.bg}`}`}>
                      <div className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white ${colors.label}`}>
                            P{pIdx + 1}
                          </span>
                          <div>
                            <input
                              type="text"
                              value={person.name}
                              onChange={(e) => setSplitPersons(prev => prev.map((p, i) => i === pIdx ? { ...p, name: e.target.value } : p))}
                              className={`font-bold text-sm bg-transparent border-b border-dashed ${colors.text} w-24 focus:outline-none`}
                              disabled={person.paymentDone}
                            />
                            <p className="text-xs text-gray-500">{person.itemIndices.length} unidad{person.itemIndices.length !== 1 ? 'es' : ''} · {
                              person.itemIndices.map(i => splitUnits[i]?.name).filter(Boolean).join(', ').substring(0, 40) || '—'
                            }</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-black ${colors.text}`}>${total.toFixed(2)}</span>
                          {person.paymentDone ? (
                            <span className="text-green-600 font-bold text-sm flex items-center gap-1">
                              <CheckCircle className="w-5 h-5" /> Pagado
                            </span>
                          ) : (
                            <div className="flex gap-1">
                              {splitPersons.length > 2 && (
                                <button onClick={() => {
                                  if (splitPersons.length <= 2) return;
                                  const orphanItems = splitPersons[pIdx].itemIndices;
                                  setSplitPersons(prev =>
                                    prev.filter((_, i) => i !== pIdx).map((p, i) =>
                                      i === 0 ? { ...p, itemIndices: [...new Set([...p.itemIndices, ...orphanItems])] } : p
                                    )
                                  );
                                }}
                                  className="text-gray-400 hover:text-red-500 text-xs px-1">✕</button>
                              )}
                              <button
                                onClick={() => { setSplitPayingPerson(isPaying ? null : pIdx); setSplitPayMethod('cash'); setSplitCash(''); setSplitMixedCashBill(''); setSplitMixedTransferBill(''); }}
                                disabled={person.itemIndices.length === 0 || total <= 0}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white transition disabled:opacity-40 ${colors.btn}`}>
                                {isPaying ? 'Cancelar' : 'Cobrar'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Formulario de pago inline */}
                      {isPaying && !person.paymentDone && (
                        <div className="border-t p-3 bg-white space-y-3">
                          <div className="flex gap-2">
                            {[
                              { id: 'cash', label: ' Efectivo' },
                              { id: 'transfer', label: ' Transfer' },
                              { id: 'mixed', label: ' Mixto' },
                            ].map(m => (
                              <button key={m.id}
                                onClick={() => { setSplitPayMethod(m.id); setSplitCash(''); setSplitMixedCashBill(''); setSplitMixedTransferBill(''); }}
                                className={`flex-1 py-1.5 border-2 rounded-lg text-xs font-semibold transition ${splitPayMethod === m.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                                {m.label}
                              </button>
                            ))}
                          </div>

                          {splitPayMethod === 'cash' && (
                            <div>
                              <label className="text-xs font-semibold text-gray-600">Efectivo recibido</label>
                              <input type="number" step="0.01" value={splitCash} onChange={(e) => setSplitCash(e.target.value)}
                                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" placeholder="0.00" autoFocus />
                              {splitCash && <p className="text-xs text-green-600 mt-1 font-semibold">Cambio: ${Math.max(0, (parseFloat(splitCash) || 0) - total).toFixed(2)}</p>}
                            </div>
                          )}
                          {splitPayMethod === 'transfer' && (
                            <div className="p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                              Transferencia de <strong>${total.toFixed(2)}</strong>
                            </div>
                          )}
                          {splitPayMethod === 'mixed' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs font-semibold text-gray-600">Efectivo</label>
                                <input type="number" step="0.01" value={splitMixedCashBill} onChange={(e) => setSplitMixedCashBill(e.target.value)}
                                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" placeholder="0.00" />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-600">Transferencia</label>
                                <input type="number" step="0.01" value={splitMixedTransferBill} onChange={(e) => setSplitMixedTransferBill(e.target.value)}
                                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" placeholder="0.00" />
                              </div>
                              {splitMixedCashBill && splitMixedTransferBill && (
                                <p className="col-span-2 text-xs text-green-600 font-semibold">
                                  Cambio: ${Math.max(0, (parseFloat(splitMixedCashBill) || 0) + (parseFloat(splitMixedTransferBill) || 0) - total).toFixed(2)}
                                </p>
                              )}
                            </div>
                          )}

                          <button
                            onClick={async () => {
                              if (splitPayMethod === 'cash' && (!splitCash || parseFloat(splitCash) < total)) {
                                showAlert('warning', 'Monto insuficiente', 'Ingresa el monto recibido correctamente');
                                return;
                              }
                              if (splitPayMethod === 'mixed') {
                                const mc = (parseFloat(splitMixedCashBill) || 0) + (parseFloat(splitMixedTransferBill) || 0);
                                if (mc < total) { showAlert('warning', 'Monto insuficiente', 'El total mixto no cubre la cuenta'); return; }
                              }
                              setSplitProcessing(true);
                              try {
                                // Recombinar unidades en items agrupados por producto
                                const assignedUnits = person.itemIndices.map(i => splitUnits[i]);
                                const grouped = {};
                                assignedUnits.forEach(u => {
                                  const key = `${u.orderId}-${u.name}-${u.price}`;
                                  if (!grouped[key]) grouped[key] = { ...u, quantity: 0, subtotal: 0 };
                                  grouped[key].quantity += 1;
                                  grouped[key].subtotal += u.price;
                                });
                                const items = Object.values(grouped);

                                const cashR = splitPayMethod === 'cash' ? parseFloat(splitCash) : splitPayMethod === 'mixed' ? (parseFloat(splitMixedCashBill) || 0) : null;
                                const transferR = splitPayMethod === 'transfer' ? total : splitPayMethod === 'mixed' ? (parseFloat(splitMixedTransferBill) || 0) : null;
                                const changeR = splitPayMethod === 'cash'
                                  ? (parseFloat(splitCash) - total)
                                  : splitPayMethod === 'mixed'
                                    ? Math.max(0, (parseFloat(splitMixedCashBill) || 0) + (parseFloat(splitMixedTransferBill) || 0) - total)
                                    : null;

                                await axios.post(
                                  `${API_URL}/api/pos/tables/${selectedTable.id}/split-pay`,
                                  {
                                    items,
                                    total,
                                    payment_method: splitPayMethod,
                                    cash_received: cashR,
                                    transfer_amount: transferR
                                  },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );

                                // ⭐ Verificar si TODAS las personas con items asignados ya pagaron
                                const updatedPersons = splitPersons.map((p, i) => i === pIdx ? { ...p, paymentDone: true } : p);
                                const allPaid = updatedPersons
                                  .filter(p => p.itemIndices.length > 0)
                                  .every(p => p.paymentDone);

                                // ⭐ No se cierra el modal de split/mesa ni se marca la persona
                                // como pagada en la UI todavia: eso se difiere a onDone, que
                                // corre despues de que el usuario decide si imprime el recibo
                                // de esta porcion.
                                setPendingReceipt({
                                  ticket_ref: `Mesa ${selectedTable.name} · ${person.name}`,
                                  items,
                                  total,
                                  payment_method: splitPayMethod,
                                  cash_received: cashR,
                                  transfer_amount: transferR,
                                  change_given: changeR,
                                  order_type: 'servir',
                                  table_label: selectedTable.name,
                                  customer_name: person.name,
                                  created_at: new Date().toISOString(),
                                  onDone: async () => {
                                    setSplitCash('');
                                    setSplitMixedCashBill('');
                                    setSplitMixedTransferBill('');
                                    setPendingReceipt(null);
                                    if (allPaid) {
                                      try {
                                        // Finalizar desglose: marcar pedidos originales y liberar mesa
                                        await axios.post(
                                          `${API_URL}/api/pos/tables/${selectedTable.id}/finalize-split`,
                                          {},
                                          { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                        showAlert('success', '¡Mesa liberada!', `Todos los items han sido cobrados. Mesa ${selectedTable.name} disponible.`);
                                        setShowSplitBillModal(false);
                                        setSelectedTable(null);
                                        setTableOrders([]);
                                      } catch (finalizeErr) {
                                        showAlert('error', 'Error', finalizeErr.response?.data?.error || 'No se pudo finalizar el desglose de la mesa');
                                      }
                                    } else {
                                      setSplitPersons(updatedPersons);
                                      setSplitPayingPerson(null);
                                      showAlert('success', 'Pago procesado', `${person.name} pagó $${total.toFixed(2)}`);
                                    }
                                    loadTables();
                                  }
                                });
                              } catch (err) {
                                showAlert('error', 'Error', err.response?.data?.error || 'No se pudo procesar el pago');
                              } finally {
                                setSplitProcessing(false);
                              }
                            }}
                            disabled={splitProcessing}
                            className="w-full py-2 bg-green-600 text-white rounded-lg font-bold text-sm hover:bg-green-700 transition disabled:opacity-60">
                            {splitProcessing ? 'Procesando...' : ` Confirmar pago de $${total.toFixed(2)}`}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Verificar unidades sin asignar */}
              {(() => {
                const assignedCount = splitPersons.reduce((sum, p) => sum + p.itemIndices.length, 0);
                if (assignedCount < splitUnits.length) {
                  return (
                    <div className="p-3 bg-orange-50 border border-orange-300 rounded-xl text-sm text-orange-700 font-semibold">
                      ⚠️ {splitUnits.length - assignedCount} unidad(es) sin asignar — asígnalas a una persona antes de cobrar.
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ⭐ MODAL: PEDIDOS SIN MESA */}
      {showUnassignedModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowUnassignedModal(false)}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold flex items-center gap-2">
                  <ClipboardList className="w-6 h-6 text-orange-600" />
                  Pedidos Sin Mesa Asignada
                </h3>
                <button
                  onClick={() => setShowUnassignedModal(false)}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full w-9 h-9 flex items-center justify-center transition"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {unassignedOrders.length} {unassignedOrders.length === 1 ? 'pedido' : 'pedidos'} pendiente{unassignedOrders.length !== 1 ? 's' : ''} de asignar
              </p>
            </div>

            <div className="p-6 space-y-4">
              {unassignedOrders.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No hay pedidos sin mesa</p>
                  <p className="text-gray-400 text-sm mt-2">Todos los pedidos tienen mesa asignada</p>
                </div>
              ) : (
                unassignedOrders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">
                          Pedido {getOrderCode(order)}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {order.customer_name && `Cliente: ${order.customer_name}  `}
                          {formatEcuadorTime(order.created_at, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-orange-600">
                          ${order.total.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 uppercase">{order.order_type}</p>
                      </div>
                    </div>

                    <div className="mb-3 space-y-1">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="text-sm text-gray-700 flex justify-between">
                          <span> {item.quantity}x {item.name}</span>
                          <span className="font-semibold">${item.subtotal.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowAssignTableModal(true);
                        }}
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition font-semibold"
                      >
                         Asignar Mesa
                      </button>
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowCancelModal(true);
                        }}
                        className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition font-semibold"
                      >
                        <XOctagon className="w-4 h-4 inline mr-1" />
                        Anular
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ⭐ MODAL: ASIGNAR MESA */}
      {showAssignTableModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold">
                  Asignar Mesa - Pedido {getOrderCode(selectedOrder)}
                </h3>
                <button
                  onClick={() => {
                    setShowAssignTableModal(false);
                    setSelectedOrder(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Total: <span className="font-bold text-orange-600">${selectedOrder.total.toFixed(2)}</span>
              </p>
            </div>

            <div className="p-6">
              <h4 className="font-semibold mb-3 text-lg">Selecciona una mesa:</h4>

              {/* Mesas Disponibles */}
              <div className="mb-6">
                <h5 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  MESAS DISPONIBLES
                </h5>
                <div className="grid grid-cols-3 gap-3">
                  {tables.filter(t => t.status === 'available').map((table) => (
                    <button
                      key={table.id}
                      onClick={() => handleAssignTable(selectedOrder.id, table.id)}
                      className="p-4 bg-green-100 border-2 border-green-500 rounded-lg hover:bg-green-200 transition text-center"
                    >
                      <Users className="w-6 h-6 mx-auto mb-1 text-green-700" />
                      <p className="font-bold text-green-900">{table.name}</p>
                    </button>
                  ))}
                </div>
                {tables.filter(t => t.status === 'available').length === 0 && (
                  <p className="text-gray-400 text-sm italic">No hay mesas disponibles</p>
                )}
              </div>

              {/* Mesas Ocupadas */}
              <div>
                <h5 className="text-sm font-semibold text-orange-700 mb-2 flex items-center gap-2">
                  <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                  MESAS OCUPADAS (agregar a cuenta existente)
                </h5>
                <div className="grid grid-cols-3 gap-3">
                  {tables.filter(t => t.status === 'occupied').map((table) => (
                    <button
                      key={table.id}
                      onClick={() => handleAssignTable(selectedOrder.id, table.id)}
                      className="p-4 bg-orange-100 border-2 border-orange-500 rounded-lg hover:bg-orange-200 transition text-center"
                    >
                      <Users className="w-6 h-6 mx-auto mb-1 text-orange-700" />
                      <p className="font-bold text-orange-900">{table.name}</p>
                      <p className="text-xs text-orange-700">{table.order_count} orden{table.order_count > 1 ? 'es' : ''}</p>
                      <p className="text-xs font-semibold text-orange-800">${table.total?.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
                {tables.filter(t => t.status === 'occupied').length === 0 && (
                  <p className="text-gray-400 text-sm italic">No hay mesas ocupadas</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ⭐ MODAL: ANULAR PEDIDO CON MOTIVO */}
      {showCancelModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-500 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="text-red-600 flex-shrink-0">
                <XOctagon className="w-12 h-12" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-red-800 mb-2 flex items-center gap-2">
                  <span>️</span>
                  Anular Pedido {getOrderCode(selectedOrder)}
                </h3>
                <p className="text-gray-700 text-sm">
                  Esta acción no se puede deshacer. El stock será restaurado y el pedido aparecerá como cancelado en reportes.
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block font-semibold mb-2 text-gray-800">
                 Motivo de la anulación <span className="text-red-600">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ej: Cliente canceló el pedido, error en la orden..."
                className="w-full px-4 py-3 border-2 border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                rows="3"
                autoFocus
              />
            </div>

            <div className="bg-yellow-100 border border-yellow-400 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-700">
                <strong>Total a anular:</strong> ${selectedOrder.total.toFixed(2)}<br />
                <strong>Items:</strong> {selectedOrder.items.length} producto{selectedOrder.items.length > 1 ? 's' : ''}
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedOrder(null);
                  setCancelReason('');
                }}
                className="px-6 py-2 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100 transition"
              >
                Volver
              </button>
              <button
                onClick={handleCancelOrder}
                className="px-6 py-2 border-2 border-red-500 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition"
              >
                Confirmar Anulación
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmRemoveItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Anular producto</h3>
            <p className="text-gray-600 text-sm mb-4">
              ¿Anular <strong>{confirmRemoveItem.quantity}x {confirmRemoveItem.name}</strong> de esta comanda? El stock se restaura.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmRemoveItem(null)} disabled={removingItem} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancelar</button>
              <button onClick={executeRemoveItem} disabled={removingItem} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50">
                {removingItem ? 'Anulando...' : 'Anular'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⭐ Editar pedido YA PAGADO (pedido de Angelo, 2026-08-05). Ver
          PUT /api/pos/sales/:id/edit-paid en el backend: exige motivo,
          recalcula total, ajusta stock por la diferencia, y todo queda
          registrado en audit_log (visible en Auditoría). Aviso explícito
          sobre el riesgo (el efectivo ya cobrado no se ajusta solo) para que
          quien edite sepa que esto es para corregir errores de registro, no
          para "hacer descuentos" después de cobrado. */}
      {editingPaidOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="p-5 border-b flex-shrink-0">
              <h3 className="text-lg font-bold text-gray-900">Editar pedido pagado</h3>
              <p className="text-xs text-gray-500 mt-1">Orden {getOrderCode(editingPaidOrder)} · Este pedido ya fue cobrado.</p>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                Editar un pedido pagado NO ajusta automáticamente el efectivo ya cobrado ni el cierre de caja de ese turno.
                Úsalo solo para corregir errores de registro. El cambio queda guardado en Auditoría con el motivo que escribas.
              </div>

              <div className="space-y-2">
                {editItems.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">Sin productos. Agrega al menos uno o cancela la edición.</p>
                ) : editItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg p-2.5 border border-gray-200">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">${Number(item.price).toFixed(2)} c/u</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => updateEditItemQty(idx, -1)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
                      >-</button>
                      <span className="w-6 text-center text-sm font-bold">{item.quantity}</span>
                      <button
                        onClick={() => updateEditItemQty(idx, 1)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
                      >+</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center border-t pt-3">
                <span className="font-semibold text-gray-700">Nuevo total:</span>
                <span className="text-xl font-bold text-green-600">${editTotal.toFixed(2)}</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de la edición <span className="text-red-500">*</span></label>
                <textarea
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: se registró una cantidad equivocada por error"
                />
              </div>
            </div>
            <div className="p-4 border-t flex gap-3 flex-shrink-0">
              <button
                onClick={() => setEditingPaidOrder(null)}
                disabled={savingPaidEdit}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePaidEdit}
                disabled={savingPaidEdit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
              >
                {savingPaidEdit ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MesasManager;
